import { routes } from '@core/routes';
import { Button, Container, Heading, Text } from '@soteria/design-system-ve';
import type { PropsWithChildren, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { BaseLayout } from './BaseLayout';
import { mainContent, footerWrapper, errorContainer, errorContent } from './Layout.css';
import { PageFooter } from './PageFooter';

/**
 * ErrorLayout props interface
 */
interface ErrorLayoutProps {
  /**
   * Error title
   */
  title?: string;

  /**
   * Error message
   */
  message?: string;

  /**
   * Custom error content
   */
  customErrorContent?: ReactNode;

  /**
   * Action button text
   */
  actionText?: string;

  /**
   * Action button handler
   */
  onAction?: () => void;

  /**
   * Whether to show a back button
   */
  showBackButton?: boolean;
}

/**
 * ErrorLayout component
 * Displays an error message with optional action buttons
 */
export function ErrorLayout({
  title = 'Something went wrong',
  message = 'An error occurred while loading this page.',
  customErrorContent,
  actionText = 'Go Home',
  onAction,
  showBackButton = true,
  children,
}: PropsWithChildren<ErrorLayoutProps>) {
  const navigate = useNavigate();

  // Default action is to navigate to community groups (main app landing)
  const handleAction = () => {
    if (onAction) {
      onAction();
    } else {
      navigate(routes.communityGroups.root);
    }
  };

  // Handle back button click
  const handleBack = () => {
    navigate(-1);
  };

  return (
    <BaseLayout
      className={mainContent}
      footer={
        <div className={footerWrapper}>
          <PageFooter />
        </div>
      }
    >
      <Container className={errorContainer}>
        <div className={errorContent}>
          {customErrorContent || (
            <>
              <Heading level="h2">{title}</Heading>
              <Text>{message}</Text>
              <div>
                <Button onClick={handleAction}>{actionText}</Button>
                {showBackButton && (
                  <Button style={{ marginLeft: '1rem' }} onClick={handleBack}>
                    Go Back
                  </Button>
                )}
              </div>
            </>
          )}
          {children}
        </div>
      </Container>
    </BaseLayout>
  );
}
